import { useEffect } from 'react'
import { useDataStore } from '../stores/dataStore'
import { useAuthStore } from '../stores/authStore'
import { 
  Users, 
  MapPin, 
  AlertTriangle, 
  Calendar, 
  TrendingUp,
  Clock,
  Shield,
  DollarSign
} from 'lucide-react'
import './Dashboard.css'

export default function Dashboard() {
  const { 
    dashboardStats, 
    fetchDashboardStats, 
    isLoading 
  } = useDataStore()

  const { user } = useAuthStore()

  useEffect(() => {
    fetchDashboardStats()
    const interval = setInterval(fetchDashboardStats, 30000) // Refresh every 30s
    return () => clearInterval(interval)
  }, [])

  const stats = dashboardStats?.stats || {}

  const statCards = [
    { 
      title: 'Total Guards', 
      value: stats.totalGuards || 0, 
      icon: Users, 
      color: 'blue',
      subtext: `${stats.activeGuards || 0} Active`
    },
    { 
      title: 'Active Sites', 
      value: stats.totalSites || 0, 
      icon: MapPin, 
      color: 'green',
      subtext: 'Operational'
    },
    { 
      title: 'Today\'s Shifts', 
      value: stats.todayShifts || 0, 
      icon: Clock, 
      color: 'yellow',
      subtext: 'Scheduled'
    },
    { 
      title: 'Active Incidents', 
      value: stats.activeIncidents || 0, 
      icon: AlertTriangle, 
      color: 'red',
      subtext: 'Require attention'
    },
    { 
      title: 'Pending Leaves', 
      value: stats.pendingLeaves || 0, 
      icon: Calendar, 
      color: 'purple',
      subtext: 'Awaiting approval'
    },
    { 
      title: 'Monthly Expenses', 
      value: `KES ${(stats.monthlyExpenses || 0).toLocaleString()}`, 
      icon: DollarSign, 
      color: 'orange',
      subtext: 'This month'
    },
  ]

  return (
    <div className="dashboard">
      {/* Welcome Section */}
      <div className="welcome-section">
        <div className="welcome-text">
          <h1>Welcome back, {user?.firstName}!</h1>
          <p>Here\'s what\'s happening with your security operations today.</p>
        </div>
        <div className="welcome-actions">
          <button className="btn btn-primary">
            <Shield size={18} />
            Live Monitoring
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {statCards.map((stat, index) => (
          <div key={index} className={`stat-card ${stat.color}`}>
            <div className="stat-icon">
              <stat.icon size={24} />
            </div>
            <div className="stat-content">
              <h3>{stat.value}</h3>
              <p>{stat.title}</p>
              <span className="stat-subtext">{stat.subtext}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* Recent Incidents */}
        <div className="dashboard-card incidents-card">
          <div className="card-header">
            <h3>Recent Incidents</h3>
            <button className="btn btn-sm btn-outline">View All</button>
          </div>
          <div className="card-body">
            {dashboardStats?.recentIncidents?.length > 0 ? (
              <div className="incidents-list">
                {dashboardStats.recentIncidents.map((incident) => (
                  <div key={incident._id} className="incident-item">
                    <div className={`severity-badge ${incident.severity}`}>
                      {incident.severity}
                    </div>
                    <div className="incident-info">
                      <h4>{incident.type.replace('_', ' ').toUpperCase()}</h4>
                      <p>{incident.site?.name}</p>
                      <span>{new Date(incident.incidentDate).toLocaleString()}</span>
                    </div>
                    <div className={`status-badge ${incident.status}`}>
                      {incident.status}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <Shield size={48} />
                <p>No recent incidents</p>
              </div>
            )}
          </div>
        </div>

        {/* Today's Shifts */}
        <div className="dashboard-card shifts-card">
          <div className="card-header">
            <h3>Today\'s Shifts</h3>
            <button className="btn btn-sm btn-outline">Manage</button>
          </div>
          <div className="card-body">
            {dashboardStats?.todaysShifts?.length > 0 ? (
              <div className="shifts-list">
                {dashboardStats.todaysShifts.map((shift) => (
                  <div key={shift._id} className="shift-item">
                    <div className="shift-time">
                      <Clock size={16} />
                      <span>{shift.startTime} - {shift.endTime}</span>
                    </div>
                    <div className="shift-site">
                      <MapPin size={16} />
                      <span>{shift.site?.name}</span>
                    </div>
                    <div className="shift-guards">
                      <Users size={16} />
                      <span>{shift.guardsAssigned?.length || 0} guards</span>
                    </div>
                    <div className={`shift-status ${shift.status}`}>
                      {shift.status}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <Clock size={48} />
                <p>No shifts scheduled for today</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          <button className="action-card">
            <AlertTriangle size={24} />
            <span>Report Incident</span>
          </button>
          <button className="action-card">
            <Users size={24} />
            <span>Add Guard</span>
          </button>
          <button className="action-card">
            <Calendar size={24} />
            <span>Schedule Shift</span>
          </button>
          <button className="action-card">
            <TrendingUp size={24} />
            <span>View Reports</span>
          </button>
        </div>
      </div>
    </div>
  )
}
